import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import type { RecommendationsServingConfig } from '@noths/polaris-client-listings-api';
import { ProductCarousel } from '@noths/polaris-client-ribbons-design-system';

import { selectSelectedOptionValues } from 'src/redux/basket/selectors';
import { selectProduct } from 'src/redux/product/selectors';
import { selectUser } from 'src/redux/user/selectors';
import { useListingsRecommendations } from '../hook/useListingsRecommendations';
import {
  trackRecommendedProductClick,
  trackRecommendedProductsCarouselNavigation,
  trackRecommendedProductsImpression,
} from '../tracking/recommendedProductCarouselEvents';

export interface RecommendedProductsCarouselContainerProps {
  readonly enableFreeDomesticDeliveryLabels?: boolean;
  readonly hideLabelIcons?: boolean;
  readonly servingConfig: RecommendationsServingConfig;
  title: string;
}

export const RecommendedProductsCarouselContainer = ({
  enableFreeDomesticDeliveryLabels,
  hideLabelIcons,
  servingConfig,
  title,
}: RecommendedProductsCarouselContainerProps) => {
  const selectedOptionValues = useSelector(selectSelectedOptionValues);
  const user = useSelector(selectUser);
  const product = useSelector(selectProduct);

  const { currencyCode, gaVisitorId: visitorId } = user;
  const partnerShortCode = product.partner.shortcode;
  const productCode = product.code.toString();

  const { recommendedProducts } = useListingsRecommendations({
    currencyCode,
    partnerShortCode,
    productCode,
    servingConfig,
    visitorId,
  });

  const onVisible = useCallback(() => {
    if (recommendedProducts?.length === 0) {
      return;
    }

    trackRecommendedProductsImpression({
      products: recommendedProducts,
      selectedOptionValues,
      servingConfig,
    });
  }, [selectedOptionValues, recommendedProducts, servingConfig]);

  const onProductClick = useCallback(
    (evt: React.MouseEvent, { index }: { index: number }) => {
      if (!recommendedProducts || recommendedProducts?.length === 0) {
        return;
      }

      trackRecommendedProductClick(evt, {
        product: recommendedProducts[index],
        productIndex: index,
        selectedOptionValues,
        servingConfig,
      });
    },
    [selectedOptionValues, recommendedProducts, servingConfig],
  );

  const onNavigation = useCallback(
    (swiped: boolean, control: 'next' | 'prev') => {
      trackRecommendedProductsCarouselNavigation({
        servingConfig,
        navigation: {
          type: swiped ? 'Scroll' : 'Arrow Click',
          direction: control === 'next' ? 'Right' : 'Left',
        },
      });
    },
    [servingConfig],
  );

  return (
    <ProductCarousel
      currency={user.currencyCode}
      enableFreeDomesticDeliveryLabels={enableFreeDomesticDeliveryLabels}
      hideLabelIcons={hideLabelIcons}
      lazyImages
      onNavigation={onNavigation}
      onProductClick={onProductClick}
      onVisible={onVisible}
      products={recommendedProducts}
      title={title}
    />
  );
};
